import { settings } from '@/config/settings';
import type { WeaponCategory } from '@/config/weapons';

/**
 * Procedural WebAudio SFX — no audio files, synthesized like our procedural
 * sprites. AudioContext is created lazily on the first call (autoplay policy:
 * the first call always happens after a user gesture, since input drives it).
 */
class Sfx {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;

  private ensure(): AudioContext | null {
    if (!settings.sound) return null;
    if (!this.ctx) {
      try {
        this.ctx = new AudioContext();
        this.master = this.ctx.createGain();
        this.master.gain.value = 0.4;
        this.master.connect(this.ctx.destination);
      } catch {
        return null;
      }
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
    return this.ctx;
  }

  /** Simple enveloped oscillator blip */
  private tone(
    freq: number,
    endFreq: number,
    dur: number,
    type: OscillatorType,
    vol: number,
    delay = 0,
  ): void {
    const ctx = this.ensure();
    if (!ctx || !this.master) return;
    const t0 = ctx.currentTime + delay;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, endFreq), t0 + dur);
    gain.gain.setValueAtTime(vol, t0);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    osc.connect(gain).connect(this.master);
    osc.start(t0);
    osc.stop(t0 + dur + 0.02);
  }

  /** Filtered noise burst (explosions, hits) */
  private noise(dur: number, vol: number, cutoff: number, delay = 0): void {
    const ctx = this.ensure();
    if (!ctx || !this.master) return;
    if (!this.noiseBuffer) {
      const len = ctx.sampleRate;
      this.noiseBuffer = ctx.createBuffer(1, len, ctx.sampleRate);
      const data = this.noiseBuffer.getChannelData(0);
      // Fixed-seed LCG so the buffer is deterministic (Math.random ban)
      let s = 0x12345678;
      for (let i = 0; i < len; i++) {
        s = (s * 1664525 + 1013904223) >>> 0;
        data[i] = (s / 0xffffffff) * 2 - 1;
      }
    }
    const t0 = ctx.currentTime + delay;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(cutoff, t0);
    filter.frequency.exponentialRampToValueAtTime(80, t0 + dur);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(vol, t0);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    src.connect(filter).connect(gain).connect(this.master);
    src.start(t0, 0, dur + 0.05);
  }

  // ---- Game events ----

  shoot(category: WeaponCategory): void {
    switch (category) {
      case 'bullet': this.tone(520, 240, 0.07, 'square', 0.12); break;
      case 'laser': this.tone(980, 620, 0.05, 'sawtooth', 0.09); break;
      case 'plasma': this.tone(260, 120, 0.12, 'triangle', 0.16); break;
      case 'bomb': this.tone(150, 60, 0.16, 'square', 0.18); break;
    }
  }

  supershot(): void {
    this.tone(220, 880, 0.18, 'sawtooth', 0.22);
    this.tone(330, 1320, 0.18, 'square', 0.12, 0.02);
    this.noise(0.2, 0.15, 2400);
  }

  explosion(scale = 1): void {
    this.noise(0.3 * scale, 0.3, 1400);
    this.tone(120 * scale, 40, 0.3 * scale, 'sine', 0.25);
  }

  bossDeath(): void {
    this.explosion(2);
    this.noise(0.8, 0.35, 900, 0.15);
    this.tone(80, 30, 1.0, 'sine', 0.3, 0.1);
  }

  playerHit(): void {
    this.tone(220, 55, 0.25, 'sawtooth', 0.3);
    this.noise(0.15, 0.2, 1800);
  }

  gem(): void {
    this.tone(1320, 1980, 0.08, 'sine', 0.1);
  }

  redGem(): void {
    this.tone(880, 1320, 0.09, 'sine', 0.14);
    this.tone(1320, 1760, 0.1, 'sine', 0.1, 0.06);
  }

  pickup(): void {
    this.tone(440, 880, 0.12, 'triangle', 0.16);
    this.tone(660, 1320, 0.12, 'triangle', 0.1, 0.07);
  }

  portal(): void {
    this.tone(180, 720, 0.5, 'sine', 0.18);
    this.noise(0.4, 0.08, 1200, 0.05);
  }

  uiMove(): void {
    this.tone(660, 660, 0.04, 'square', 0.05);
  }

  uiConfirm(): void {
    this.tone(520, 780, 0.09, 'square', 0.1);
  }

  uiDeny(): void {
    this.tone(220, 160, 0.12, 'square', 0.1);
  }

  recordToast(): void {
    this.tone(660, 990, 0.1, 'triangle', 0.12);
    this.tone(990, 1480, 0.14, 'triangle', 0.12, 0.09);
  }
}

export const sfx = new Sfx();
